import {
    Button,
    makeStyles,
    Menu,
    MenuButtonProps,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    mergeClasses,
    SlotRenderFunction,
    SplitButton,
    tokens,
    Tooltip,
} from '@fluentui/react-components';
import { useBoolean } from '@fluentui/react-hooks';
import {
    Checkmark20Regular,
    Delete20Regular,
    Dismiss20Regular,
    Folder20Filled,
    MoreHorizontal20Filled,
    Rename20Regular,
} from '@fluentui/react-icons';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRenameTaxReturnFolderRecord, useSaveTaxReturnFolderRecord } from '../../api/report/report';
import { IFilterItem } from '../../data-types/filterItem';
import { useFeedbackMessages } from '../../hooks/useFeedbackMessages';
import { useFunctionalAccess } from '../../hooks/useFunctionalAccess';
import { customPalette } from '../../theme';
import { scrollToElement, scrollToFolderElementByName } from '../../utils/ContextualMenuUtils';
import { getFilterItemDisplayLabel } from '../../utils/FilterUtils';
import { handleFolderCreation, isInvalidFolderName } from '../../utils/FoldersUtils';
import { validNameRuleRegExp } from '../../utils/RegExpUtils';
import CustomTextField from './TextField';

export interface IContextualMenuFolderItemProps {
    folder?: IFilterItem;
    isSelected?: boolean;
    dismissMenu: () => void;
    updateFolderList?: () => Promise<any>;
    onFolderChange?: (filteredItems: IFilterItem[], filterName: string) => void;
    onCancel?: () => void;
    onDeleteRequest?: (folder: IFilterItem) => void;
    isNewFolder?: boolean;
    onSaveFolder?: () => void;
    itemIndex: number;
    filterName?: string;
}

const useStyles = makeStyles({
    editRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0px 4px',
        gap: tokens.spacingHorizontalXS,
        width: '100%',
    },
    editGrow: {
        flexGrow: 1,
        minWidth: 0,
    },
    splitButton: {
        width: '100%',
        height: '44px',
        minWidth: '0',
    },
    folderButton: {
        width: '100%',
        justifyContent: 'flex-start',
        color: customPalette.blueDark,
        // Keep adjoining edge squared so chrome styles do not undo SplitButton join.
        borderTopRightRadius: '0',
        borderBottomRightRadius: '0',
    },
    folderIcon: {
        color: customPalette.yellowDark,
    },
    overflowButton: {
        minWidth: '44px',
        // Keep adjoining edge squared so chrome styles do not undo SplitButton join.
        borderTopLeftRadius: '0',
        borderBottomLeftRadius: '0',
        '> .fui-MenuButton__menuIcon': {
            height: 'initial',
            width: 'initial',
            display: 'flex',
            alignItems: 'center',
        },
    },
    disabledMenuItem: {
        opacity: 0.5,
    },
});

const ContextualMenuFolderItem: React.FC<IContextualMenuFolderItemProps> = ({
    folder,
    isSelected: _isSelected = false,
    dismissMenu,
    updateFolderList,
    onFolderChange,
    onCancel,
    onDeleteRequest,
    isNewFolder = false,
    onSaveFolder,
    itemIndex,
    filterName = '',
}) => {
    const { t } = useTranslation();
    const styles = useStyles();
    const { mutateAsync: renameFolderCall, isLoading: renameFolderIsLoading } = useRenameTaxReturnFolderRecord();
    const { displayFeedbackMessage } = useFeedbackMessages();
    const { mutateAsync: saveFolder, isLoading: saveFolderIsLoading } = useSaveTaxReturnFolderRecord();
    const [isEdit, { setTrue: setIsEdit, setFalse: clearIsEdit }] = useBoolean(isNewFolder);
    const [isErrorInput, { setTrue: setIsErrorInput, setFalse: clearIsErrorInput }] = useBoolean(false);
    const folderKey = folder?.key ?? 'new-folder';
    const displayLabel = folder ? getFilterItemDisplayLabel(folder) : '';
    const trimmedFolderName = displayLabel.replace(/\(\d+\)$/, '');
    const [folderNameValue, setFolderNameValue] = useState<string>(folder?.searchBy?.[0] || '');
    const menuItemContainerRef = useRef<HTMLDivElement>(null);
    const isSaving = saveFolderIsLoading || renameFolderIsLoading;
    const { functionalAccess } = useFunctionalAccess();

    const canRenameFolder = functionalAccess?.editFolderButtonEnabled;
    const canDeleteFolder = functionalAccess?.deleteFolderButtonEnabled;

    const finishEditing = (nextFolderKey: string | undefined, scrollByName = false) => {
        if (nextFolderKey) {
            updateFolderList &&
                updateFolderList().then(() => {
                    if (scrollByName) {
                        scrollToFolderElementByName(nextFolderKey);
                    } else {
                        scrollToElement(nextFolderKey);
                    }
                    setFolderNameValue(trimmedFolderName);
                    clearIsEdit();
                });
        }
    };

    const saveChanges = () => {
        if (isInvalidFolderName(folderNameValue)) {
            return;
        }

        if (isNewFolder) {
            saveFolder({ params: { folderName: folderNameValue } }).then((response) => {
                handleFolderCreation(
                    response,
                    folderNameValue,
                    t('newFolderCreated'),
                    displayFeedbackMessage,
                    setIsErrorInput,
                    menuItemContainerRef,
                    finishEditing,
                    clearIsErrorInput,
                    onSaveFolder
                );
            });
        } else {
            renameFolderCall({ params: { FolderKey: Number(folderKey), FolderName: folderNameValue } }).then((response) => {
                handleFolderCreation(
                    response,
                    folderKey,
                    t('folderRenamedSuccessfully'),
                    displayFeedbackMessage,
                    setIsErrorInput,
                    menuItemContainerRef,
                    finishEditing,
                    clearIsErrorInput,
                    onSaveFolder
                );
            });
        }
    };

    const handleChangeFolderName = (ev: any) => {
        if (validNameRuleRegExp.test(ev.target.value)) {
            setFolderNameValue(ev.target.value);
        }
    };

    const handleCancel = () => {
        clearIsEdit();
        scrollToElement(folderKey);
        clearIsErrorInput();
    };

    const handleDeleteRequest = () => {
        if (folder) {
            onDeleteRequest?.(folder);
        }
    };

    const handleChangeSelectedFolder = () => {
        if (folder && onFolderChange) {
            onFolderChange([folder], filterName || folder.label);
            dismissMenu();
        }
    };

    const cancelRenameIfNecessary = (event: FocusEvent) => {
        if (!menuItemContainerRef.current?.contains(event.relatedTarget as Node)) {
            clearIsEdit();
            clearIsErrorInput();
            if (onCancel) onCancel();
        }
    };

    const inputName = isNewFolder ? t('addFolderTextField') : t('renameFolderTextField');
    const folderOptionsLabel = t('folderOptions').toString();

    const overflowMenuList = (
        <MenuList>
            {canRenameFolder ? (
                <MenuItem icon={<Rename20Regular />} onClick={setIsEdit}>
                    {t('rename').toString()}
                </MenuItem>
            ) : (
                <Tooltip content={t('noFunctionalAccess').toString()} relationship='description' appearance='inverted' withArrow>
                    <MenuItem icon={<Rename20Regular />} disabled className={styles.disabledMenuItem}>
                        {t('rename').toString()}
                    </MenuItem>
                </Tooltip>
            )}
            {canDeleteFolder ? (
                <MenuItem icon={<Delete20Regular />} onClick={handleDeleteRequest}>
                    {t('Delete').toString()}
                </MenuItem>
            ) : (
                <Tooltip content={t('noFunctionalAccess').toString()} relationship='description' appearance='inverted' withArrow>
                    <MenuItem icon={<Delete20Regular />} disabled className={styles.disabledMenuItem}>
                        {t('Delete').toString()}
                    </MenuItem>
                </Tooltip>
            )}
        </MenuList>
    );

    const renderOverflowMenuButton: SlotRenderFunction<MenuButtonProps> = useCallback(
        ((Component, props) => (
            <Tooltip content={folderOptionsLabel} relationship='description' appearance='inverted' withArrow positioning='before'>
                <Component {...(props as Record<string, unknown>)} />
            </Tooltip>
        )) as SlotRenderFunction<MenuButtonProps>,
        [folderOptionsLabel]
    );

    return (
        <div ref={menuItemContainerRef}>
            {isEdit ? (
                <div className={styles.editRow} key={folderKey} id={folderKey}>
                    <Folder20Filled className={styles.folderIcon} />
                    <div className={styles.editGrow}>
                        <CustomTextField
                            id={`renameFolderInput${folderKey}`}
                            onChange={handleChangeFolderName}
                            value={folderNameValue}
                            ariaLabel={inputName}
                            onBlur={(event) => cancelRenameIfNecessary(event.nativeEvent)}
                            onEnter={saveChanges}
                            isProcessing={isSaving}
                            isErrorInput={isErrorInput}
                            processingLabel={t('processing').toString()}
                        />
                    </div>
                    <Button
                        appearance='subtle'
                        icon={<Checkmark20Regular />}
                        aria-label={t('confirmRenameFolder').toString()}
                        onClick={saveChanges}
                        onBlur={(event: React.FocusEvent<HTMLButtonElement>) => cancelRenameIfNecessary(event.nativeEvent)}
                        disabled={isSaving || isInvalidFolderName(folderNameValue)}
                    />
                    <Button
                        appearance='subtle'
                        icon={<Dismiss20Regular />}
                        aria-label={t('cancelRenameFolder').toString()}
                        onClick={onCancel ? onCancel : handleCancel}
                        onBlur={(event: React.FocusEvent<HTMLButtonElement>) => cancelRenameIfNecessary(event.nativeEvent)}
                        disabled={isSaving}
                    />
                </div>
            ) : (
                <div key={folderKey} tabIndex={0}>
                    <Menu>
                        <MenuTrigger disableButtonEnhancement>
                            {(triggerProps: MenuButtonProps) => (
                                <SplitButton
                                    appearance='subtle'
                                    className={styles.splitButton}
                                    icon={<Folder20Filled className={styles.folderIcon} />}
                                    menuIcon={<MoreHorizontal20Filled />}
                                    primaryActionButton={{
                                        id: `item-${itemIndex}`,
                                        className: styles.folderButton,
                                        'aria-label': displayLabel,
                                        onClick: handleChangeSelectedFolder,
                                    }}
                                    menuButton={{
                                        ...triggerProps,
                                        'aria-label': folderOptionsLabel,
                                        className: mergeClasses(styles.overflowButton, triggerProps.className),
                                        children: renderOverflowMenuButton,
                                    }}
                                >
                                    {displayLabel}
                                </SplitButton>
                            )}
                        </MenuTrigger>
                        <MenuPopover>{overflowMenuList}</MenuPopover>
                    </Menu>
                </div>
            )}
        </div>
    );
};

export default ContextualMenuFolderItem;
